import { type } from "@testing-library/user-event/dist/type";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllDepartments } from "../../../services/AdminServices";
import { getAttendanceSummaryRange } from "../../../services/AppService";
import MessageModal from "../../../components/AdminComponents/Modals/MessageModal";
import PageFade from "../../../components/Login/PageFade"
import closeButton from "../../../assets/images/close.png";
import { useTranslation } from "react-i18next";


const AttendanceSummary = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const today = new Date().toISOString().split('T')[0];

    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [deptId, setDeptId] = useState('All');
    const [departments, setDepartments] = useState([]);
    const [summary, setSummary] = useState([]);
    const [loading, setLoading] = useState(false);

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "error",
        title: "",
        message: ""
    });

    const showAlert = (title, message, type = "error") => {
        setMessageModal({ open: true, type, title, message });
    }


    const loadDepartments = useCallback(async () => {
        try {
            const data = await getAllDepartments();
            if (data) {
                setDepartments(data.data);
            } else {
                setDepartments([]);
            }
        } catch (error) {
            console.error("Dept Fetch Error:", error);
            showAlert("Configuration Error", "Could not load department list. Please refresh the page.");
        }
    }, []);


    useEffect(() => { loadDepartments(); }, [loadDepartments]);

    const handleFetch=async()=>{
        // 1. Frontend Validation: Date Range
        if (new Date(fromDate) > new Date(toDate)) {
            showAlert("Invalid Range", " 'From Date' cannot be later than 'To Date'.");
            return;
        }

        // 2. Frontend Validation: Future Dates
        if (new Date(toDate) > new Date(today)) {
            showAlert("Invalid Date", "Reports cannot be generated for future dates.");
            return;
        }

        try{
            setLoading(true);
            setSummary([]);
            
            const data=await getAttendanceSummaryRange(fromDate, toDate, deptId);
            if (!data || data.length === 0) {
                showAlert("No Records", "No attendance data found for the selected criteria.", "info");
            } else {
                setSummary(data);
            }
        }catch (error) {
            console.error("Fetch Error:", error);
            const errorMsg = error.response?.data?.message || "Internal server error occurred while fetching summary.";
            showAlert("Fetch Failed", errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const calculateTotal=(key)=>{
        if(!summary.length) return 0;
        return summary.reduce((acc, curr)=> acc + (Number(curr?.[key]) || 0), 0);
    }

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <div className="flex justify-between items-center border-b pb-3 mb-6">
                    <h4 className="text-xl font-bold text-[#2c5282] tracking-tight">{t('attendance.attendanceSummary')}</h4>
                    <img src={closeButton} className="w-6 h-6 cursor-pointer hover:opacity-70 transition-opacity" alt="Close" onClick={() => navigate(-1)} />
                </div>

                {/* Range Filters */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-8 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-black text-gray-500 uppercase ml-1">{t('attendance.fromDate')} <span className="text-red-500">*</span></label>
                            <input type="date" max={today} value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border-2 border-slate-200 p-2 rounded-lg bg-white font-semibold text-gray-700 outline-none focus:border-blue-400" />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-black text-gray-500 uppercase ml-1">{t('attendance.toDate')} <span className="text-red-500">*</span></label>
                            <input type="date" max={today} value={toDate} onChange={(e) => setToDate(e.target.value)} className="border-2 border-slate-200 p-2 rounded-lg bg-white font-semibold text-gray-700 outline-none focus:border-blue-400" />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-black text-gray-500 uppercase ml-1">{t('attendance.department')} <span className="text-red-500">*</span></label>
                            <select value={deptId} onChange={(e) => setDeptId(e.target.value)} className="border-2 border-slate-200 p-2 rounded-lg bg-white font-semibold text-gray-700 outline-none focus:border-blue-400">
                                <option value="All">All Departments</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-center mt-6">
                        <button 
                            onClick={handleFetch} 
                            disabled={loading}
                            className="bg-[#2c5282] text-white px-14 py-2.5 rounded-xl font-bold uppercase shadow-lg hover:bg-[#1a365d] transition-all disabled:opacity-50 active:scale-95"
                        >
                            {loading ? "Generating Report..." : (t('attendance.showSummary'))}
                        </button>
                    </div>
                </div>

                {/* Summary Table Section */}
                {summary.length > 0 ? (
                    <div className="overflow-hidden border border-slate-200 rounded-lg shadow-md animate-in fade-in duration-500">
                        <div className="overflow-x-auto">
                            <table className="w-full text-center border-collapse">
                                <thead className="bg-[#a0adf1] text-white uppercase">
                                    <tr>
                                        <th className="p-3 border-r border-blue-300 w-12">{t('tables.srNo')}</th>
                                        <th className="p-3 border-r border-blue-300 text-left">{t('attendance.department')}</th>
                                        <th className="p-3 border-r border-blue-300 bg-green-600">{t('attendance.totalStaff')}</th>
                                        <th className="p-3 border-r border-blue-300">{t('attendance.onTime')}</th>
                                        <th className="p-3 border-r border-blue-300">{t('attendance.lateMark')}</th>
                                        <th className="p-3 border-r border-blue-300">{t('attendance.onDuty')}</th>
                                        <th className="p-3 border-r border-blue-300 bg-blue-600">{t('attendance.totalPresent')}</th>
                                        <th className="p-3 border-r border-blue-300">{t('attendance.leave')}</th>
                                        <th className="p-3 border-r border-blue-300">{t('attendance.absent')}</th>
                                        <th className="p-3 bg-red-600">{t('attendance.totalAbsent')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 bg-white">
                                    {summary.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                                            <td className="p-2 border-r border-slate-100 font-bold text-gray-400">{idx + 1}</td>
                                            <td className="p-2 border-r border-slate-100 text-left font-bold text-blue-800">{item.departmentName || "N/A"}</td>
                                            <td className="p-2 border-r border-slate-100 bg-green-50 font-black text-green-800">{item.totalStaff || 0}</td>
                                            <td className="p-2 border-r border-slate-100 font-medium">{item.onTime || 0}</td>
                                            <td className="p-2 border-r border-slate-100 font-medium">{item.lateMark || 0}</td>
                                            <td className="p-2 border-r border-slate-100 font-medium">{item.onDuty || 0}</td>
                                            <td className="p-2 border-r border-slate-100 bg-blue-50 font-black text-blue-800">{item.totalPresent || 0}</td>
                                            <td className="p-2 border-r border-slate-100 font-medium">{item.leave || 0}</td>
                                            <td className="p-2 border-r border-slate-100 font-medium">{item.absent || 0}</td>
                                            <td className="p-2 bg-red-50 font-black text-red-800">{item.totalNotPresent || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-slate-100 font-black text-slate-700 uppercase border-t border-gray-200">
                                    <tr>
                                        <td colSpan={2} className="p-4 border-r border-slate-200 text-right">{t('attendance.grandTotal')}</td>
                                        <td className="p-4 border-r border-slate-200 text-green-800 bg-green-100/50">{calculateTotal('totalStaff')}</td>
                                        <td className="p-4 border-r border-slate-200">{calculateTotal('onTime')}</td>
                                        <td className="p-4 border-r border-slate-200">{calculateTotal('lateMark')}</td>
                                        <td className="p-4 border-r border-slate-200">{calculateTotal('onDuty')}</td>
                                        <td className="p-4 border-r border-slate-200 text-blue-800 bg-blue-100/50">{calculateTotal('totalPresent')}</td>
                                        <td className="p-4 border-r border-slate-200">{calculateTotal('leave')}</td>
                                        <td className="p-4 border-r border-slate-200">{calculateTotal('absent')}</td>
                                        <td className="p-4 text-red-800 bg-red-100/50">{calculateTotal('totalNotPresent')}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                ) : !loading && (
                    <div className="text-center py-20 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 font-medium">
                        {t('attendance.clickOnShowSummary')}
                    </div>
                )}
            </div>

            {/* Error/Information Modal */}
            <MessageModal 
                open={messageModal.open} 
                type={messageModal.type} 
                title={messageModal.title} 
                message={messageModal.message} 
                onClose={() => setMessageModal({ ...messageModal, open: false })} 
            />
        </PageFade>
    )
}

export default AttendanceSummary;